import {
  MicrophonePermissionStatus,
  type RecordingOutput,
} from "../../hooks/useMicrophone";

export enum VoiceRecordingState {
  PrePermission = "pre-permission",
  ReadyToRecord = "ready-to-record",
  ActiveRecording = "active-recording",
  RecordingComplete = "recording-complete",
  Submitted = "submitted",
}

export enum VoiceAction {
  PermissionGranted = "permission-granted",
  PermissionDenied = "permission-denied",
  StartRecording = "start-recording",
  RecordingReady = "recording-ready",
  RecordingFailed = "recording-failed",
  DeleteRecording = "delete-recording",
  SubmitRecording = "submit-recording",
  SubmitSuccess = "submit-success",
  SubmitFailed = "submit-failed",
}

interface PermissionGrantedAction {
  type: VoiceAction.PermissionGranted;
}
interface PermissionDeniedAction {
  type: VoiceAction.PermissionDenied;
}
interface StartRecordingAction {
  type: VoiceAction.StartRecording;
}
interface RecordingReadyAction {
  type: VoiceAction.RecordingReady;
  payload: RecordingOutput;
}
interface RecordingFailedAction {
  type: VoiceAction.RecordingFailed;
}
interface DeleteRecordingAction {
  type: VoiceAction.DeleteRecording;
}
interface SubmitRecordingAction {
  type: VoiceAction.SubmitRecording;
}
interface SubmitSuccessAction {
  type: VoiceAction.SubmitSuccess;
}
interface SubmitFailedAction {
  type: VoiceAction.SubmitFailed;
}

export type VoiceActionPayload =
  | PermissionGrantedAction
  | PermissionDeniedAction
  | StartRecordingAction
  | RecordingReadyAction
  | RecordingFailedAction
  | DeleteRecordingAction
  | SubmitRecordingAction
  | SubmitSuccessAction
  | SubmitFailedAction;

export interface VoiceStateMachineState {
  phase: VoiceRecordingState;
  permissionStatus: MicrophonePermissionStatus;
  recordingOutput: RecordingOutput | undefined;
  isUploading: boolean;
}

export const createInitialVoiceState = (
  resetCount: number,
): VoiceStateMachineState => ({
  // After a successful recording + discard, skip straight to ReadyToRecord
  // since we know permissions are granted. This avoids UI flicker.
  phase:
    resetCount > 0
      ? VoiceRecordingState.ReadyToRecord
      : VoiceRecordingState.PrePermission,
  permissionStatus:
    resetCount > 0
      ? MicrophonePermissionStatus.Granted
      : MicrophonePermissionStatus.Prompt,
  recordingOutput: undefined,
  isUploading: false,
});

export const voiceReducer = (
  state: VoiceStateMachineState,
  action: VoiceActionPayload,
): VoiceStateMachineState => {
  switch (action.type) {
    case VoiceAction.PermissionGranted: {
      if (state.phase !== VoiceRecordingState.PrePermission) {
        return state;
      }
      return {
        ...state,
        phase: VoiceRecordingState.ReadyToRecord,
        permissionStatus: MicrophonePermissionStatus.Granted,
      };
    }

    case VoiceAction.PermissionDenied: {
      if (
        state.phase !== VoiceRecordingState.PrePermission &&
        state.phase !== VoiceRecordingState.ReadyToRecord
      ) {
        return state;
      }
      return {
        ...state,
        phase: VoiceRecordingState.PrePermission,
        permissionStatus: MicrophonePermissionStatus.Denied,
      };
    }

    case VoiceAction.StartRecording: {
      if (state.phase !== VoiceRecordingState.ReadyToRecord) {
        return state;
      }
      return {
        ...state,
        phase: VoiceRecordingState.ActiveRecording,
        recordingOutput: undefined,
      };
    }

    case VoiceAction.RecordingReady: {
      if (state.phase !== VoiceRecordingState.ActiveRecording) {
        return state;
      }
      return {
        ...state,
        phase: VoiceRecordingState.RecordingComplete,
        recordingOutput: action.payload,
      };
    }

    case VoiceAction.RecordingFailed: {
      if (state.phase !== VoiceRecordingState.ActiveRecording) {
        return state;
      }
      return {
        ...state,
        phase: VoiceRecordingState.ReadyToRecord,
        recordingOutput: undefined,
      };
    }

    case VoiceAction.DeleteRecording: {
      if (
        state.phase !== VoiceRecordingState.RecordingComplete ||
        state.isUploading
      ) {
        return state;
      }
      return {
        ...state,
        phase: VoiceRecordingState.ReadyToRecord,
        recordingOutput: undefined,
      };
    }

    case VoiceAction.SubmitRecording: {
      if (state.phase !== VoiceRecordingState.RecordingComplete) {
        return state;
      }
      return { ...state, isUploading: true };
    }

    case VoiceAction.SubmitSuccess: {
      if (!state.isUploading) {
        return state;
      }
      return {
        ...state,
        phase: VoiceRecordingState.Submitted,
        isUploading: false,
      };
    }

    case VoiceAction.SubmitFailed: {
      if (!state.isUploading) {
        return state;
      }
      return { ...state, isUploading: false };
    }

    default:
      return state;
  }
};
